import { Api } from './api';
import { EventType, TrackedEvent } from './types';
import { dlog, pageUrl } from './util';

// Behavioral event batcher (docs/widget.md §2): flush every 5s or at 20 events
// (the server's ArrayMaxSize), keepalive flush on page hide. Only page_view and
// widget_open feed StatDaily today; the rest are trigger-tuning material.

const FLUSH_INTERVAL_MS = 5_000;
const MAX_BATCH = 20;

export class EventBatcher {
  private queue: TrackedEvent[] = [];

  constructor(
    private readonly api: Api,
    private readonly getAnonId: () => string | undefined,
  ) {}

  start(): void {
    setInterval(() => void this.flush(), FLUSH_INTERVAL_MS); // lives as long as the page

    // pagehide fires on both navigation and tab close (unlike unload, which
    // mobile Safari skips); visibilitychange:hidden covers app-switch on mobile
    window.addEventListener('pagehide', () => this.flushSync());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flushSync();
    });
  }

  track(type: EventType, props?: Record<string, unknown>): void {
    this.queue.push({ type, pageUrl: pageUrl(), props });
    if (this.queue.length >= MAX_BATCH) void this.flush();
  }

  private takeBatch(): TrackedEvent[] {
    return this.queue.splice(0, MAX_BATCH);
  }

  private async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    const events = this.takeBatch();
    try {
      await this.api.post('/public/events', { anonId: this.getAnonId(), events });
    } catch (err) {
      dlog('event flush failed (dropped)', err);
      // dropped, not requeued: analytics events are not worth retry loops
    }
  }

  /** page is going away — hand the batch to keepalive fetch and return */
  private flushSync(): void {
    if (this.queue.length === 0) return;
    this.api.flushOnUnload({ anonId: this.getAnonId(), events: this.takeBatch() });
  }
}

/**
 * SPA route detection (docs/widget.md §4): patch pushState/replaceState and
 * listen to popstate. Fires the callback only when the URL actually changed.
 */
export function patchHistory(onRouteChange: () => void): void {
  let lastUrl = pageUrl();
  const check = () => {
    // let the framework finish its own routing tick first
    setTimeout(() => {
      const url = pageUrl();
      if (url !== lastUrl) {
        lastUrl = url;
        try {
          onRouteChange();
        } catch (err) {
          dlog('route-change handler failed', err);
        }
      }
    }, 0);
  };

  try {
    const origPush = history.pushState.bind(history);
    history.pushState = (...args: Parameters<History['pushState']>) => {
      origPush(...args);
      check();
    };
    const origReplace = history.replaceState.bind(history);
    history.replaceState = (...args: Parameters<History['replaceState']>) => {
      origReplace(...args);
      check();
    };
  } catch {
    /* frozen history object — SPA tracking simply off */
  }
  window.addEventListener('popstate', check);
}
